/**
 * StockValuationWidget — iter173 dashboard widget.
 *
 * Compact card showing today's cost value + retail value + margin, with a
 * link through to the full report. Uses landed cost by default (fastest) but
 * the parent Dashboard can pass config.cost_method to override.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Receipt, TrendUp, ArrowRight } from '@phosphor-icons/react';
import { getStockValuation } from '../lib/api';

const fmtMoney = (n) => (n == null ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const fmtPct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

export default function StockValuationWidget({ costMethod = 'landed' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await getStockValuation({ cost_method: costMethod });
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [costMethod]);

  const t = data?.totals;
  const marginGood = (t?.margin_pct ?? 0) >= 20;

  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="widget-stock-valuation">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-widest text-foreground flex items-center gap-2">
          <Coins size={16} weight="duotone" className="text-brand" />
          Stock Valuation
        </h3>
        <Link
          to="/reports/stock-valuation"
          className="text-xs text-brand hover:underline flex items-center gap-1"
          data-testid="widget-stock-valuation-link"
        >
          Full report <ArrowRight size={11} />
        </Link>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground py-6 text-center">Loading valuation…</p>
      ) : !t ? (
        <p className="text-xs text-muted-foreground py-6 text-center">No data available.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Cell
              label={`Cost · ${costMethod === 'weighted_avg' ? 'W-avg' : costMethod === 'fifo' ? 'FIFO' : 'Landed'}`}
              value={fmtMoney(t.active_cost_value)}
              icon={Coins}
              testid="widget-stock-cost"
            />
            <Cell
              label="Retail"
              value={fmtMoney(t.retail_value)}
              icon={Receipt}
              testid="widget-stock-retail"
            />
            <Cell
              label="Margin"
              value={fmtMoney(t.margin_value)}
              hint={fmtPct(t.margin_pct)}
              hintGood={marginGood}
              icon={TrendUp}
              testid="widget-stock-margin"
            />
          </div>
          <p className="mt-3 text-[10px] text-muted-foreground tracking-wide">
            {data.total_skus.toLocaleString()} SKUs · {Number(t.on_hand).toLocaleString(undefined, { maximumFractionDigits: 0 })} units on hand
          </p>
        </>
      )}
    </div>
  );
}

const Cell = ({ label, value, hint, hintGood, icon: Icon, testid }) => (
  <div className="bg-muted/40 border border-border/60 px-3 py-2 rounded" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <Icon size={12} className="text-muted-foreground" />
    </div>
    <p className="mt-1 text-lg font-bold font-mono tabular-nums truncate" title={value}>{value}</p>
    {hint != null && (
      <p className={`text-[10px] font-bold font-mono ${hintGood ? 'text-emerald-700' : 'text-amber-700'}`}>{hint}</p>
    )}
  </div>
);
